import { CurrencyCode, Weight } from '@rematter/frontend-lib-api-client';

import { toMoney } from '../utils/money';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type BookingLoadForInvoice = {
  id: string;
  documentNumber: string;
  status: string;
  facilityId: string;
  accountId: string | null;
  accountName: string | null;
  paymentTerm: string | null;
  reconciliation: {
    id: string;
    invoiceId: string | null;
  } | null;
  lineItems: Array<{
    id: string;
    itemId: string | null;
    item: {
      id: string;
      name: string;
      skuNumber: string | null;
    } | null;
    phase: string | null;
    weights: Record<string, Weight> | null;
    unitPrice: { amount: number; currencyCode: CurrencyCode } | null;
    pricingUnit: string | null;
  }>;
};

export type BookingLoadsForInvoiceResult = {
  bookingId: string;
  bookingNumber: string;
  loads: BookingLoadForInvoice[];
};

export const bookingLoadsForInvoiceQueryAtom = createQueryAtomFactory(
  'trade.bookingLoadsForInvoice',
  (params: { bookingId: string }) => ({
    bookings: {
      __args: {
        filter: { ids: [params.bookingId] },
        pagination: { limit: 1, offset: 0 },
      },
      entries: {
        id: true,
        documentNumber: true,
      },
    },
    loads: {
      __args: {
        filter: { bookingIds: [params.bookingId] },
      },
      entries: {
        id: true,
        documentNumber: true,
        status: true,
        facilityId: true,
        salesOrderId: true,
        salesOrder: {
          id: true,
          account: {
            id: true,
            name: true,
          },
          paymentTerm: true,
        },
        reconciliation: {
          id: true,
          invoiceId: true,
        },
        lineItems: {
          id: true,
          itemId: true,
          item: {
            id: true,
            name: true,
            skuNumber: true,
          },
          phase: true,
          weights: true,
          unitPrice: {
            amount: true,
            currencyCode: true,
          },
          pricingUnit: true,
        },
      },
    },
  }),
  {
    select: (data): BookingLoadsForInvoiceResult | null => {
      const booking = data?.bookings?.entries?.[0];
      const loads = data?.loads?.entries;

      if (!booking || !loads) {
        return null;
      }

      return {
        bookingId: booking.id,
        bookingNumber: booking.documentNumber,
        loads: loads.map((load) => ({
          id: load.id,
          documentNumber: load.documentNumber.toString(),
          status: load.status,
          facilityId: load.facilityId,
          accountId: load.salesOrder?.account?.id ?? null,
          accountName: load.salesOrder?.account?.name ?? null,
          paymentTerm: load.salesOrder?.paymentTerm ?? null,
          reconciliation: load.reconciliation
            ? {
                id: load.reconciliation.id,
                invoiceId: load.reconciliation.invoiceId ?? null,
              }
            : null,
          lineItems: load.lineItems.map((lineItem) => ({
            id: lineItem.id,
            itemId: lineItem.itemId,
            item: lineItem.item ?? null,
            phase: lineItem.phase ?? null,
            weights: lineItem.weights as Record<string, Weight> | null,
            unitPrice: lineItem.unitPrice
              ? (toMoney(lineItem.unitPrice) as {
                  amount: number;
                  currencyCode: CurrencyCode;
                })
              : null,
            pricingUnit: lineItem.pricingUnit ?? null,
          })),
        })),
      };
    },
  },
);
